// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/components/MeasuredBody.tsx (ADAPT)
// ADAPTED (plan P4-2, Step 4.2): TP's tables (`ADS_TABLE`, `FUNNEL_CHECKS`, `TIKTOK_TABLE`, `RETENTION_TABLE`, `OMNI_TABLE`)
// and ad metrics (`OWN_METRICS`, `stats`) are Shapermint sample fixtures. Ours reads the BFF's "how we measured it" rows
// (`vm.measuredFor`, display strings) and its dot strips (`vm.strips`). TP's intro sentences now say only what each part
// looks at: the ones that described how we gather the data (a shopper walking every cohort brand's checkout, an inbox signed
// up to each brand, a data vendor) claimed work we do not do, and the sample "9 of 16" went. The Omnichannel channel table
// has no BFF source, so it shows the BFF's rows too.
/**
 * "How we measured it": per dimension, where the numbers come from and the table (or, for Ads, the strips) behind its
 * checks. Search & AI shows its four blocks instead (SearchBlocks).
 */
import { MessageCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { Badge } from '@/components/ui/shadcn/badge'
import { Card } from '@/components/ui/shadcn/card'
import type { DimensionKey } from '../growth-score.types'
import { useGrowthScore } from '../GrowthScoreContext'
import { DotStrip } from './charts'
import { CheckName, KvTable } from './visuals'

const CHANNEL_LABEL: Record<string, string> = { meta: 'Meta', google: 'Google', tiktok: 'TikTok', linkedin: 'LinkedIn' }

/** An answer from the onboarding, quoted where it changes what we measured. */
export function FromYourAnswer({ children }: { children: ReactNode }) {
  return (
    <Badge intent="info" title="From your answer">
      <MessageCircle aria-hidden className="size-3.5" />
      {children}
    </Badge>
  )
}

/** The BFF's rows for one dimension as a Check / You / Cohort median / Rank table; a line when it has none. */
function MeasuredTable({ dimension, caption }: { dimension: DimensionKey; caption: string }) {
  const vm = useGrowthScore()
  const rows = vm.measuredFor(dimension)
  if (rows.length === 0) return <p className="text-body-sm text-ds-text-2">There are no rows for this part yet.</p>
  return <KvTable caption={caption} rows={rows.map((r): [ReactNode, string, string, string] => [<CheckName key={r.check} name={r.name} sub={r.sub} />, r.you, r.cohortMedian, r.rank])} />
}

export function MeasuredBody({ dimension }: { dimension: DimensionKey }) {
  const vm = useGrowthScore()
  const channels = (vm.profile.channelMix ?? []).map((c) => CHANNEL_LABEL[c.channel] ?? c.channel)
  const order = channels.join(' → ')

  const body = (() => {
    switch (dimension) {
      case 'ads':
        return (
          <>
            <p className="text-body-sm text-foreground">
              {vm.profile.runsPaidAds && channels.length ? (
                <>
                  Paid channels, in the order you gave <FromYourAnswer>{order}</FromYourAnswer>.
                </>
              ) : (
                <>This compares your ads with your cohort.</>
              )}
            </p>
            {vm.strips.length > 0 ? (
              <>
                <p className="text-body-sm text-foreground">Each dot is a competitor; the larger one is you.</p>
                <div className="flex flex-col divide-y divide-ds-hairline">
                  {vm.strips.map((s) => (
                    <div key={s.key} className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-4 py-2.5">
                      <div className="flex flex-col">
                        <span className="text-body-sm text-foreground">{s.label}</span>
                        <span className="text-label text-ds-text-3">{s.sub}</span>
                      </div>
                      <DotStrip values={s.stats.values} you={s.you} median={s.stats.median} unit={s.unit} label={s.label} />
                      <div className="flex flex-col items-end">
                        <span className="text-metric-sm text-foreground">
                          {s.you}
                          {s.unit}
                        </span>
                        <span className="text-label text-ds-text-2">
                          #{s.stats.rank} of {s.stats.n}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            <MeasuredTable dimension="ads" caption="Ads checks" />
          </>
        )
      case 'funnel': {
        const upsell = vm.copy.upsell
        return (
          <>
            <p className="text-body-sm text-foreground">
              The funnel covers how you raise order value, where your ads land, the steps to payment, the offer after payment and
              subscriptions.{' '}
              {upsell === 'yes' ? (
                <>
                  You told us you have a one-click upsell after payment <FromYourAnswer>Upsell: yes</FromYourAnswer>.
                </>
              ) : upsell === 'no' ? (
                <>
                  You said you have no post-purchase upsell <FromYourAnswer>Upsell: no</FromYourAnswer>.
                </>
              ) : null}
            </p>
            <MeasuredTable dimension="funnel" caption="Funnel checks" />
          </>
        )
      }
      case 'tiktok':
        return (
          <>
            <p className="text-body-sm text-foreground">
              Your own account, the creators who sell or talk about you, TikTok Shop and the TikTok Ads (Spark) that boost them.
            </p>
            <MeasuredTable dimension="tiktok" caption="TikTok checks" />
          </>
        )
      case 'email':
        return (
          <>
            <p className="text-body-sm text-foreground">
              Retention covers the welcome series, what happens when a cart is left, how often the offer is a discount, and whether the
              site asks for a phone number.
            </p>
            <MeasuredTable dimension="email" caption="Retention checks" />
            <p className="text-label text-ds-text-3">A check with no score yet doesn&apos;t count.</p>
          </>
        )
      case 'omni':
        return (
          <>
            <p className="text-body-sm text-foreground">
              Where your competitors buy traffic beyond Meta, and how much of yours depends on it.{' '}
              {vm.profile.runsPaidAds && order ? (
                <>
                  You told us <FromYourAnswer>{order}</FromYourAnswer>.
                </>
              ) : null}
            </p>
            <MeasuredTable dimension="omni" caption="Paid channels" />
          </>
        )
      default:
        return null
    }
  })()

  return <Card className="flex flex-col gap-4 p-5">{body}</Card>
}
